
import { useParams, Navigate, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useEpisodes } from '../../../hooks/useEpisodes';
import { useAuth } from '../../../hooks/useAuth';
import SEOHead from '../../../components/seo/SEOHead';
import SchemaOrg from '../../../components/seo/SchemaOrg';

export default function EpisodeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasAccess, loading, subscriberLoading } = useAuth();
  const { episodes, loading: episodesLoading, incrementViews } = useEpisodes();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showResources, setShowResources] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [viewIncremented, setViewIncremented] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Find the specific episode by id
  const episode = episodes?.find(ep => ep.id?.toString() === id);
  
  // Get current episode index for navigation
  const currentEpisodeIndex = episodes?.findIndex(ep => ep.id?.toString() === id) ?? -1;
  const nextEpisode = currentEpisodeIndex >= 0 && currentEpisodeIndex < episodes.length - 1 
    ? episodes[currentEpisodeIndex + 1] 
    : null;
  const prevEpisode = currentEpisodeIndex > 0 
    ? episodes[currentEpisodeIndex - 1] 
    : null;

  // Combined loading state - wait for ALL to complete
  const isStillLoading = loading || subscriberLoading || episodesLoading;

  // Only initialize after everything is fully loaded
  useEffect(() => {
    if (!isStillLoading && !isInitialized) {
      setIsInitialized(true);
      
      // Increment view count only after everything is loaded and we have access
      if (episode?.id && hasAccess && !viewIncremented) {
        incrementViews(episode.id);
        setViewIncremented(true);
      }
    }
  }, [isStillLoading, isInitialized, episode?.id, hasAccess, viewIncremented, incrementViews]);

  // Show loading until everything is ready
  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-loader-4-line animate-spin text-white text-2xl"></i>
          </div>
          <p className="text-gray-600">Loading episode...</p>
        </div>
      </div>
    );
  }

  // Only check access after everything is initialized
  if (!hasAccess) {
    return <Navigate to="/subscribe" replace />;
  }

  // Handle case where episode is not found
  if (!episode) {
    return <Navigate to="/404" replace />;
  }

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    // In a real implementation, you would control actual media playback here
  };

  const handleSkipForward = () => {
    if (nextEpisode) {
      navigate(`/episodes/${nextEpisode.id}`);
    }
  };

  const handleSkipBackward = () => {
    if (prevEpisode) {
      navigate(`/episodes/${prevEpisode.id}`);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const allGuests = [
    episode?.guest,
    episode?.guest2,
    episode?.guest3,
    episode?.guest4,
    episode?.guest5
  ].filter(Boolean);

  const allGuestLinks = [
    { name: episode?.guest, linkedin: episode?.guestLinkedin },
    { name: episode?.guest2, linkedin: episode?.guestLinkedin2 },
    { name: episode?.guest3, linkedin: episode?.guestLinkedin3 },
    { name: episode?.guest4, linkedin: episode?.guestLinkedin4 },
    { name: episode?.guest5, linkedin: episode?.guestLinkedin5 }
  ].filter(guest => guest.name);

  const resources = [
    { name: episode?.resource1Name, url: episode?.resource1Url },
    { name: episode?.resource2Name, url: episode?.resource2Url },
    { name: episode?.resource3Name, url: episode?.resource3Url }
  ].filter(resource => resource.name && resource.url);

  const categories = [
    episode?.category,
    episode?.category2,
    episode?.category3,
    episode?.category4,
    episode?.category5
  ].filter(Boolean);

  // SEO Schema.org data for episode detail page
  const episodeSchema = {
    "@context": "https://schema.org",
    "@type": "PodcastEpisode",
    "name": episode.title,
    "description": episode.description,
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/episodes/${episode.id}`,
    "datePublished": episode.date,
    "duration": episode.duration,
    "image": episode.advertisingImage,
    "audio": {
      "@type": "AudioObject",
      "contentUrl": episode.audioUrl,
      "duration": episode.duration
    },
    "partOfSeries": {
      "@type": "PodcastSeries",
      "name": "DBD Podcast",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
      "description": "Premium business podcast featuring deep conversations about entrepreneurship, leadership, and industry expertise."
    },
    "publisher": {
      "@type": "Organization",
      "name": "DBD Podcast",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`
    },
    "creator": allGuestLinks.map(guest => ({
      "@type": "Person",
      "name": guest.name,
      "sameAs": guest.linkedin ? [guest.linkedin] : undefined
    })).filter(creator => creator.name),
    "genre": categories,
    "keywords": categories.join(', '),
    "associatedMedia": {
      "@type": "MediaObject",
      "contentUrl": episode.audioUrl,
      "duration": episode.duration,
      "encodingFormat": "audio/mpeg"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Episodes",
        "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/episodes`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": episode.title,
        "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/episodes/${episode.id}`
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <SEOHead 
        title={`${episode.title} - DBD Podcast`}
        description={episode.description}
        keywords={`${categories.join(', ')}, ${allGuests.join(', ')}, podcast episode, business insights`}
        ogTitle={episode.title}
        ogDescription={episode.description}
        ogImage={episode.advertisingImage}
        ogType="article"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/episodes/${episode.id}`}
      />
      <SchemaOrg data={episodeSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      
      {/* Hero Section with Background Image */}
      <div 
        className="relative bg-cover bg-center py-20"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url(${episode.advertisingImage})`
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/episodes" 
            className="inline-flex items-center space-x-2 text-amber-200 hover:text-white mb-8 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            <span>Back to Episodes</span>
          </Link>
          
          <div className="max-w-4xl">
            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map((category, index) => (
                <span 
                  key={index}
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-amber-500/20 text-amber-200 border border-amber-300/30"
                >
                  {category}
                </span>
              ))}
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {episode.title}
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              {episode.description}
            </p>
            
            <div className="flex flex-wrap items-center gap-8 text-gray-300">
              <div className="flex items-center space-x-3">
                <i className="ri-calendar-line text-amber-400"></i>
                <span className="text-lg">{new Date(episode.date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-time-line text-amber-400"></i>
                <span className="text-lg">{episode.duration}</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-eye-line text-amber-400"></i>
                <span className="text-lg">{episode.viewCount || 0} views</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Media Player - Supports both Audio and Video */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-gray-900">Now Playing</h2>
                <div className="flex items-center space-x-4 text-lg text-gray-600">
                  <span>{formatTime(currentTime)}</span>
                  <span>/</span>
                  <span>{episode.duration}</span>
                </div>
              </div>
              
              {episode.media_type === 'video' && episode.video_url ? (
                // Video Player
                <div className="space-y-6">
                  <div className="relative bg-black rounded-xl overflow-hidden">
                    <video
                      className="w-full aspect-video"
                      controls
                      poster={episode.advertisingImage}
                      preload="metadata"
                    >
                      <source src={episode.video_url} type="video/mp4" />
                      <source src={episode.video_url} type="video/webm" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-video-line text-white text-2xl"></i>
                    </div>
                    
                    <div className="flex-1">
                      <div className="text-xl font-bold text-gray-800 mb-2">
                        {episode.title}
                      </div>
                      <div className="text-gray-600">
                        Video Episode with {allGuests.join(', ')}
                      </div>
                      <div className="flex items-center space-x-4 mt-3 text-sm text-gray-500">
                        <span className="flex items-center space-x-1">
                          <i className="ri-play-line"></i>
                          <span>Video Format</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <i className="ri-time-line"></i>
                          <span>{episode.duration}</span>
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <button className="p-3 text-gray-600 hover:text-amber-600 transition-colors cursor-pointer" title="Share">
                        <i className="ri-share-line text-xl"></i>
                      </button>
                      <button className="p-3 text-gray-600 hover:text-amber-600 transition-colors cursor-pointer" title="Download">
                        <i className="ri-download-line text-xl"></i>
                      </button>
                      <button className="p-3 text-gray-600 hover:text-amber-600 transition-colors cursor-pointer" title="Fullscreen">
                        <i className="ri-fullscreen-line text-xl"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Audio Player (Original Design)
                <div className="flex items-center space-x-8">
                  <button 
                    onClick={handlePlayPause}
                    className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-full w-20 h-20 flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer"
                  >
                    <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-3xl`}></i>
                  </button>
                  
                  <div className="flex-1">
                    <div className="bg-gray-200 rounded-full h-3 mb-4">
                      <div 
                        className="bg-gradient-to-r from-amber-500 to-amber-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${(currentTime / (duration || 1)) * 100}%` }}
                      ></div>
                    </div>
                    <div className="text-lg font-medium text-gray-800">
                      {episode.title}
                    </div>
                    <div className="text-gray-600">
                      Audio Episode with {allGuests.join(', ')}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <button 
                      onClick={handleSkipBackward}
                      disabled={!prevEpisode}
                      className={`p-3 transition-colors cursor-pointer ${
                        prevEpisode 
                          ? 'text-gray-600 hover:text-amber-600' 
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                      title={prevEpisode ? `Previous: ${prevEpisode.title}` : 'No previous episode'}
                    >
                      <i className="ri-skip-back-fill text-2xl"></i>
                    </button>
                    <button 
                      onClick={handleSkipForward}
                      disabled={!nextEpisode}
                      className={`p-3 transition-colors cursor-pointer ${
                        nextEpisode 
                          ? 'text-gray-600 hover:text-amber-600' 
                          : 'text-gray-400 cursor-not-allowed'
                      }`}
                      title={nextEpisode ? `Next: ${nextEpisode.title}` : 'No next episode'}
                    >
                      <i className="ri-skip-forward-fill text-2xl"></i>
                    </button>
                    <button className="p-3 text-gray-600 hover:text-amber-600 transition-colors cursor-pointer">
                      <i className="ri-volume-up-line text-2xl"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Available On Section - NEW */}
            {(episode.spotify_url || episode.youtube_url) && (
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Available On</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {episode.spotify_url && (
                    <a
                      href={episode.spotify_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center space-x-4 p-6 bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border border-green-200 rounded-xl transition-all duration-300 cursor-pointer"
                    >
                      <div className="w-16 h-16 bg-green-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <i className="ri-spotify-fill text-white text-2xl"></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-green-800 mb-1">Listen on Spotify</h3>
                        <p className="text-sm text-green-600">Stream this episode on Spotify</p>
                      </div>
                      <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center group-hover:bg-green-500 transition-colors duration-300">
                        <i className="ri-external-link-line text-green-600 group-hover:text-white transition-colors duration-300"></i>
                      </div>
                    </a>
                  )}

                  {episode.youtube_url && (
                    <a
                      href={episode.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center space-x-4 p-6 bg-gradient-to-r from-amber-50 to-amber-100 hover:from-amber-100 hover:to-amber-200 border border-amber-200 rounded-xl transition-all duration-300 cursor-pointer"
                    >
                      <div className="w-16 h-16 bg-amber-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <i className="ri-youtube-fill text-white text-2xl"></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-amber-800 mb-1">Watch on YouTube</h3>
                        <p className="text-sm text-amber-600">Watch the video version on YouTube</p>
                      </div>
                      <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center group-hover:bg-amber-500 transition-colors duration-300">
                        <i className="ri-external-link-line text-amber-600 group-hover:text-white transition-colors duration-300"></i>
                      </div>
                    </a>
                  )}
                </div>
                
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-center">
                  <p className="text-sm text-amber-800">
                    <i className="ri-information-line mr-2"></i>
                    Choose your preferred platform to enjoy this episode with the best experience for you.
                  </p>
                </div>
              </div>
            )}

            {/* Show Notes */}
            {episode.showNotes && (
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Show Notes</h2>
                <div className="prose prose-lg max-w-none">
                  <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                    {episode.showNotes}
                  </div>
                </div>
              </div>
            )}

            {/* Transcript */}
            {episode.transcript && (
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-3xl font-bold text-gray-900">Transcript</h2>
                  <button
                    onClick={() => setShowTranscript(!showTranscript)}
                    className="flex items-center space-x-2 text-amber-600 hover:text-amber-700 transition-colors cursor-pointer"
                  >
                    <span className="text-lg font-medium">{showTranscript ? 'Hide' : 'Show'} Transcript</span>
                    <i className={`ri-arrow-${showTranscript ? 'up' : 'down'}-s-line text-xl`}></i>
                  </button>
                </div>
                
                {showTranscript && (
                  <div className="prose prose-lg max-w-none">
                    <div className="whitespace-pre-line text-gray-700 leading-relaxed bg-gray-50 p-8 rounded-xl">
                      {episode.transcript}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-8">
            {/* Guest Information */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {allGuests.length > 1 ? 'Featured Guests' : 'Featured Guest'}
              </h3>
              
              <div className="space-y-6">
                {allGuestLinks.map((guest, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-voice-fill text-white text-2xl"></i>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-gray-900 mb-2">{guest.name}</h4>
                      {guest.linkedin && (
                        <a 
                          href={guest.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium transition-colors cursor-pointer"
                        >
                          <i className="ri-linkedin-fill"></i>
                          <span>Connect on LinkedIn</span>
                          <i className="ri-external-link-line text-sm"></i>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Resources */}
            {resources.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">Resources</h3>
                  <button
                    onClick={() => setShowResources(!showResources)}
                    className="flex items-center space-x-2 text-amber-600 hover:text-amber-700 transition-colors cursor-pointer"
                  >
                    <i className={`ri-arrow-${showResources ? 'up' : 'down'}-s-line text-xl`}></i>
                  </button>
                </div>
                
                {(showResources || resources.length <= 3) && (
                  <div className="space-y-4">
                    {resources.map((resource, index) => (
                      <a
                        key={index}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-amber-50 hover:border-amber-200 border border-transparent transition-all duration-300 cursor-pointer group"
                      >
                        <div className="w-12 h-12 bg-amber-100 group-hover:bg-amber-200 rounded-lg flex items-center justify-center transition-colors">
                          <i className="ri-external-link-line text-amber-600 text-xl"></i>
                        </div>
                        <div className="flex-1">
                          <span className="font-medium text-gray-900 group-hover:text-amber-700 transition-colors">{resource.name}</span>
                        </div>
                        <i className="ri-arrow-right-line text-gray-400 group-hover:text-amber-600 group-hover:translate-x-1 transition-all"></i>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Episode Stats */}
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Episode Stats</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <i className="ri-eye-line text-amber-600 text-xl"></i>
                    <span className="text-gray-700">Views</span>
                  </div>
                  <span className="text-xl font-bold text-gray-900">{episode.viewCount || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <i className={`${episode.media_type === 'video' ? 'ri-video-line' : 'ri-headphone-line'} text-amber-600 text-xl`}></i>
                    <span className="text-gray-700">Format</span>
                  </div>
                  <span className="text-xl font-bold text-gray-900 capitalize">{episode.media_type || 'Audio'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <i className="ri-time-line text-amber-600 text-xl"></i>
                    <span className="text-gray-700">Duration</span>
                  </div>
                  <span className="text-xl font-bold text-gray-900">{episode.duration}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <i className="ri-calendar-line text-amber-600 text-xl"></i>
                    <span className="text-gray-700">Published</span>
                  </div>
                  <span className="text-xl font-bold text-gray-900">{new Date(episode.date).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Episodes */}
        {episode.relatedEpisodes && episode.relatedEpisodes.length > 0 && (
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Episodes</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {episode.relatedEpisodes.map(relatedId => {
                const relatedEpisode = episodes.find(ep => ep.id === relatedId);
                if (!relatedEpisode) return null;
                
                return (
                  <Link
                    key={relatedId}
                    to={`/episodes/${relatedId}`}
                    className="group bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-w-16 aspect-h-9">
                      <img
                        src={relatedEpisode.advertisingImage}
                        alt={relatedEpisode.title}
                        className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors line-clamp-2">
                        {relatedEpisode.title}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-2">{relatedEpisode.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                            <i className="ri-user-voice-line text-white text-sm"></i>
                          </div>
                          <span className="text-sm font-medium text-gray-700">{relatedEpisode.guest}</span>
                        </div>
                        <i className="ri-arrow-right-line text-amber-600 group-hover:translate-x-1 transition-transform"></i>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
